import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type { DashboardSummary, ComplianceData, Deployment, PaginatedResponse } from '@/lib/types';

const FIVE_MINUTES = 1000 * 60 * 5;

export function useDashboardSummary() {
  return useQuery({
    queryKey: queryKeys.dashboard.summary(),
    queryFn: async (): Promise<DashboardSummary> => {
      const response = await apiClient.get<DashboardSummary>('/api/v1/reports/dashboard/');
      return response.data;
    },
    refetchInterval: FIVE_MINUTES,
    staleTime: FIVE_MINUTES,
  });
}

export function useComplianceData() {
  return useQuery({
    queryKey: queryKeys.compliance.data(),
    queryFn: async (): Promise<ComplianceData> => {
      const response = await apiClient.get<ComplianceData>('/api/v1/compliance/');
      return response.data;
    },
    refetchInterval: FIVE_MINUTES,
    staleTime: FIVE_MINUTES,
  });
}

export function useRecentDeployments(limit: number = 5) {
  return useQuery({
    queryKey: [...queryKeys.deployments.lists(), 'recent', limit],
    queryFn: async (): Promise<Deployment[]> => {
      const response = await apiClient.get<PaginatedResponse<Deployment>>('/api/v1/deployments/', {
        params: {
          page_size: limit,
          ordering: '-timestamp',
        },
      });
      return response.data.results;
    },
    refetchInterval: FIVE_MINUTES,
    staleTime: FIVE_MINUTES,
  });
}

export interface AssetStatusDistribution {
  name: string;
  count: number;
}

export function useAssetStatusDistribution() {
  return useQuery({
    queryKey: [...queryKeys.assets.summary(), 'distribution'],
    queryFn: async (): Promise<AssetStatusDistribution[]> => {
      const response = await apiClient.get<{
        total_assets: number;
        by_status: Record<string, number>;
      }>('/api/v1/assets/summary/');
      
      const statusLabels: Record<string, string> = {
        available: 'Available',
        deployed: 'Deployed',
        damaged: 'Damaged',
        retired: 'Retired',
        active: 'Active',
        lost: 'Lost',
        quarantined: 'Quarantined',
      };
      
      return Object.entries(response.data.by_status).map(([status, count]) => ({
        name: statusLabels[status] || status,
        count,
      }));
    },
    refetchInterval: FIVE_MINUTES,
    staleTime: FIVE_MINUTES,
  });
}
